import { Chart } from 'chart.js';
import type { AnnotationOptions, AnnotationPluginOptions } from 'chartjs-plugin-annotation';
import { isEqual, mapValues, omit } from 'lodash';

declare module 'chart.js' {
  interface Chart {
    $indicatorLines?: Record<string, ChartIndicatorLine>;
  }
}

export type ChartIndicatorLine = {
  /** Index on the x axis the vertical line is anchored to. */
  dataIndex: number;
  /** Any canvas color, or a `var(--custom-property)` reference. */
  color: string;
};

const LINE_WIDTH = 2;
const LINE_DASH = [4, 4];
const CSS_VARIABLE_PATTERN = /^var\((--[\w-]+)\)$/;

/** The canvas cannot resolve CSS custom properties, so `var(--x)` colors are read off the document root. */
const resolveColor = (color: string) => {
  const variable = CSS_VARIABLE_PATTERN.exec(color.trim())?.[1];
  if (!variable) return color;
  return getComputedStyle(document.documentElement).getPropertyValue(variable).trim() || color;
};

const toAnnotation = ({ dataIndex, color }: ChartIndicatorLine): AnnotationOptions<'line'> => ({
  type: 'line',
  scaleID: 'x',
  value: dataIndex,
  borderWidth: LINE_WIDTH,
  borderDash: LINE_DASH,
  borderColor: () => resolveColor(color),
  adjustScaleRange: false,
  drawTime: 'afterDatasetsDraw',
});

export const buildIndicatorAnnotations = (chart?: Chart | null): Record<string, AnnotationOptions<'line'>> =>
  mapValues(chart?.$indicatorLines ?? {}, toAnnotation);

const applyIndicatorAnnotations = (chart: Chart) => {
  // A destroyed chart has no context left to draw on.
  if (!chart.ctx) return;

  const { plugins } = chart.options;
  if (plugins) {
    plugins.annotation = {
      ...plugins.annotation,
      annotations: buildIndicatorAnnotations(chart),
    } as AnnotationPluginOptions;
  }

  chart.update('none');
};

/** Adds (or moves) the vertical indicator line registered under `id` and redraws the chart. */
export const setChartIndicatorLine = (chart: Chart, id: string, line: ChartIndicatorLine) => {
  const lines = chart.$indicatorLines ?? {};
  if (isEqual(lines[id], line)) return;

  chart.$indicatorLines = { ...lines, [id]: line };
  applyIndicatorAnnotations(chart);
};

export const removeChartIndicatorLine = (chart: Chart, id: string) => {
  const lines = chart.$indicatorLines;
  if (!lines?.[id]) return;

  chart.$indicatorLines = omit(lines, id);
  applyIndicatorAnnotations(chart);
};
